const STORAGE_KEY = 'ap-physics-progress';

export interface ConceptProgress {
  lessonCompleted: boolean;
  lessonStep: number;
  quizScores: { date: string; score: number; total: number }[];
  bestQuizScore: number;
}

export interface ProgressData {
  [key: string]: ConceptProgress; // key is "unitId:conceptId"
}

function getProgress(): ProgressData {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored ? JSON.parse(stored) : {};
}

function saveProgress(data: ProgressData): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function getKey(unitId: string, conceptId: string): string {
  return `${unitId}:${conceptId}`;
}

export function getConceptProgress(unitId: string, conceptId: string): ConceptProgress {
  const data = getProgress();
  const key = getKey(unitId, conceptId);
  return data[key] || { lessonCompleted: false, lessonStep: 0, quizScores: [], bestQuizScore: 0 };
}

export function saveLessonStep(unitId: string, conceptId: string, step: number): void {
  const data = getProgress();
  const key = getKey(unitId, conceptId);
  const current = data[key] || { lessonCompleted: false, lessonStep: 0, quizScores: [], bestQuizScore: 0 };
  current.lessonStep = step;
  data[key] = current;
  saveProgress(data);
}

export function markLessonComplete(unitId: string, conceptId: string): void {
  const data = getProgress();
  const key = getKey(unitId, conceptId);
  const current = data[key] || { lessonCompleted: false, lessonStep: 0, quizScores: [], bestQuizScore: 0 };
  current.lessonCompleted = true;
  data[key] = current;
  saveProgress(data);
}

export function saveQuizScore(unitId: string, conceptId: string, score: number, total: number): void {
  const data = getProgress();
  const key = getKey(unitId, conceptId);
  const current = data[key] || { lessonCompleted: false, lessonStep: 0, quizScores: [], bestQuizScore: 0 };
  current.quizScores.push({ date: new Date().toISOString(), score, total });
  const pct = Math.round((score / total) * 100);
  if (pct > current.bestQuizScore) {
    current.bestQuizScore = pct;
  }
  data[key] = current;
  saveProgress(data);
}

export function getUnitProgress(unitId: string, conceptIds: string[]): { completed: number; total: number; avgScore: number } {
  const data = getProgress();
  let completed = 0;
  let totalScore = 0;
  let scoredConcepts = 0;

  for (const conceptId of conceptIds) {
    const key = getKey(unitId, conceptId);
    const cp = data[key];
    if (cp?.lessonCompleted) completed++;
    if (cp?.bestQuizScore > 0) {
      totalScore += cp.bestQuizScore;
      scoredConcepts++;
    }
  }

  return {
    completed,
    total: conceptIds.length,
    avgScore: scoredConcepts > 0 ? Math.round(totalScore / scoredConcepts) : 0,
  };
}

export function getAllProgress(): ProgressData {
  return getProgress();
}

export function resetProgress(): void {
  localStorage.removeItem(STORAGE_KEY);
}
